import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { downgradeInjectable } from '@angular/upgrade/static';
import * as angular from 'angular';
import { mergeMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { AppState } from '../../store';
import { selectAccountId } from '../../account/store/account.selectors';
import { AnyConnection, ConnectionAccount, GA4Account } from '../connection.models';
import { AnyComponentData, SchemaResponse } from '../../package-designer/package.models';
import { DesignerSchemaFieldI } from '../../package-designer/models/designer-schema-field.model';

interface QueryParams extends HttpParams {}

@Injectable({
  providedIn: 'root',
})
export class ConnectionItemsResource {
  private apiRoot: string = process.env.API_URL;

  constructor(private http: HttpClient, private store: Store<AppState>) {}

  get(type: string, connection_id: number, params?: QueryParams): Observable<AnyConnection> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<AnyConnection>(`${this.apiRoot}/${account_id}/api/connections/${type}/${connection_id}`, {
          params,
        }),
      ),
    );
  }

  save(type: string, body: Partial<AnyConnection>, params?: QueryParams): Observable<AnyConnection> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.post<AnyConnection>(`${this.apiRoot}/${account_id}/api/connections/${type}`, body, { params }),
      ),
    );
  }

  update(
    type: string,
    connection_id: number,
    body: Partial<AnyConnection>,
    params?: QueryParams,
  ): Observable<AnyConnection> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.put<AnyConnection>(`${this.apiRoot}/${account_id}/api/connections/${type}/${connection_id}`, body, {
          params,
        }),
      ),
    );
  }

  remove(type: string, connection_id: number, params?: QueryParams): Observable<any> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.delete<AnyComponentData>(`${this.apiRoot}/${account_id}/api/connections/${type}/${connection_id}`, {
          params,
        }),
      ),
    );
  }

  query(type: string, params?: QueryParams): Observable<AnyComponentData[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<AnyComponentData[]>(`${this.apiRoot}/${account_id}/api/connections/${type}`, { params }),
      ),
    );
  }

  schema(type: string, connection_id: number, body: any, params?: QueryParams): Observable<SchemaResponse> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) => {
        const url =
          connection_id && type !== 'curl'
            ? `${this.apiRoot}/${account_id}/api/connections/${type}/${connection_id}/schema`
            : `${this.apiRoot}/${account_id}/api/connections/${type}/schema`;
        return this.http.post<SchemaResponse>(url, body, { params, headers: { ignoreLoadingBar: '' } });
      }),
    );
  }

  test(
    type: string,
    connection_id: number | undefined,
    body: any,
    params?: QueryParams,
  ): Observable<AnyComponentData[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) => {
        const url = connection_id
          ? `${this.apiRoot}/${account_id}/api/connections/${type}/${connection_id}/test`
          : `${this.apiRoot}/${account_id}/api/connections/${type}/test`;

        return this.http.post<AnyComponentData[]>(url, body, { params, headers: { ignoreLoadingBar: '' } });
      }),
    );
  }

  accounts(type: string, connection_id: number, params?: QueryParams): Observable<ConnectionAccount[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<ConnectionAccount[]>(
          `${this.apiRoot}/${account_id}/api/connections/metadata/${type}/${connection_id}/accounts`,
          { params, headers: { ignoreLoadingBar: '' } },
        ),
      ),
    );
  }

  properties(type: string, connection_id: number, params?: QueryParams): Observable<GA4Account[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<GA4Account[]>(
          `${this.apiRoot}/${account_id}/api/connections/metadata/${type}/${connection_id}/properties`,
          { params, headers: { ignoreLoadingBar: '' } },
        ),
      ),
    );
  }

  objects(type: string, connection_id: number, params?: QueryParams): Observable<any> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<any>(`${this.apiRoot}/${account_id}/api/connections/metadata/${type}/${connection_id}/objects`, {
          params,
          headers: { ignoreLoadingBar: '' },
        }),
      ),
    );
  }

  fields(type: string, connection_id: number, params?: QueryParams): Observable<any> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<any>(`${this.apiRoot}/${account_id}/api/connections/metadata/${type}/${connection_id}/fields`, {
          params,
          headers: { ignoreLoadingBar: '' },
        }),
      ),
    );
  }

  writableFields(
    type: string,
    connection_id: number,
    params?: QueryParams,
  ): Observable<{ fields: DesignerSchemaFieldI[] }> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<{ fields: DesignerSchemaFieldI[] }>(
          `${this.apiRoot}/${account_id}/api/connections/metadata/${type}/${connection_id}/writable_fields`,
          { params, headers: { ignoreLoadingBar: '' } },
        ),
      ),
    );
  }

  relationKeys(
    type: string,
    connection_id: number,
    params?: QueryParams,
  ): Observable<{ keys: DesignerSchemaFieldI[] }> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<{ keys: DesignerSchemaFieldI[] }>(
          `${this.apiRoot}/${account_id}/api/connections/metadata/${type}/${connection_id}/relation_keys`,
          { params, headers: { ignoreLoadingBar: '' } },
        ),
      ),
    );
  }

  fieldsAsArray(type: string, connection_id: number, params?: QueryParams): Observable<AnyComponentData[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<AnyComponentData[]>(
          `${this.apiRoot}/${account_id}/api/connections/metadata/${type}/${connection_id}/fields`,
          { params, headers: { ignoreLoadingBar: '' } },
        ),
      ),
    );
  }

  customers(type: string, connection_id: number, params?: QueryParams): Observable<ConnectionAccount[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<ConnectionAccount[]>(
          `${this.apiRoot}/${account_id}/api/connections/metadata/${type}/${connection_id}/customers`,
          { params, headers: { ignoreLoadingBar: '' } },
        ),
      ),
    );
  }

  ga4Fields(api_version: string = 'v1beta', params?: QueryParams): Observable<GA4Field[]> {
    return this.http.get<GA4Field[]>(`${this.apiRoot}/ga4/${api_version}.json`, {
      params,
      headers: { ignoreLoadingBar: '' },
    });
  }

  customFields(type: string, connection_id: number, params?: QueryParams): Observable<ConnectionAccount[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<ConnectionAccount[]>(
          `${this.apiRoot}/${account_id}/api/connections/metadata/${type}/${connection_id}/customfields`,
          { params, headers: { ignoreLoadingBar: '' } },
        ),
      ),
    );
  }
}

interface GA4Field {
  dataType: string;
  id: string;
  type: string;
  uiName: string;
}

((window as any).xplenty as angular.IModule).service(
  'ConnectionItemsResource',
  downgradeInjectable(ConnectionItemsResource),
);
